export const UNKNOWN_SPEAKER_IMAGE_URL = 'https://upload.wikimedia.org/wikipedia/commons/b/bc/Unknown_person.jpg';
export const UNIDENTIFIED_SPEAKER_LABEL = 'Unidentified Speaker';

// Presentation groups within one episode, never speaker registry identities.
export const ANONYMOUS_AVATAR_PALETTE = [
    { name: 'blue', background: '#DCEBFF', foreground: '#173E73' },
    { name: 'amber', background: '#F6E3C5', foreground: '#633F10' },
    { name: 'purple', background: '#E6E0FA', foreground: '#4A2F75' },
    { name: 'green', background: '#D8F0E7', foreground: '#18513B' },
    { name: 'rose', background: '#F7DFE8', foreground: '#722743' },
    { name: 'teal', background: '#DDF0F3', foreground: '#20505B' },
    { name: 'brown', background: '#EEE6D8', foreground: '#58452D' },
    { name: 'slate', background: '#E1E8EA', foreground: '#344B55' },
];
export const UNRESOLVED_AVATAR = { name: 'neutral', background: '#ECEFF2', foreground: '#59616C' };

export const isUnknownSpeakerId = (id) => id == null || id === '' || id === 'unknown';

export const getAnonymousDisplay = (value) => {
    if (!value || !['anonymous', 'unresolved'].includes(value.kind)
        || typeof value.key !== 'string' || !value.key.length
        || value.key.length > 1024 || Array.from(value.key).length > 512
        || value.label !== 'Unidentified speaker' || value.identityConfirmed !== false) {
        return null;
    }
    const palette = value.kind === 'anonymous' ? ANONYMOUS_AVATAR_PALETTE : [UNRESOLVED_AVATAR];
    const avatar = palette.find((color) => value.avatar && color.name === value.avatar.name
        && color.background === value.avatar.background && color.foreground === value.avatar.foreground);
    return avatar ? { ...value, avatar } : null;
};

export const transcriptSpeakerKey = (message) => {
    const display = isUnknownSpeakerId(message?.speakerId) ? getAnonymousDisplay(message?.anonymousDisplay) : null;
    return display ? `anonymous:${display.key}` : `speaker:${typeof message?.speakerId}:${message?.speakerId}`;
};

export const shouldShowTranscriptSpeaker = (index, messages) => (
    index === 0 || transcriptSpeakerKey(messages[index - 1]) !== transcriptSpeakerKey(messages[index])
);

export const getSpeakerData = (speakerId, speakersData) => {
    if (!speakersData) {
        return null;
    }
    for (let i = 0; i < speakersData.length; i++) {
        const speakerData = speakersData[i]
        if (speakerData.id === speakerId) {
            return speakerData;
        }
    }
    return null;
}

export const getUnidentifiedSpeakerData = () => ({
    id: 'unknown',
    name: 'unknown',
    imageURL: UNKNOWN_SPEAKER_IMAGE_URL,
    canonicalURLs: [],
    moreInfoURLs: [],
    description: '',
});

export const getDisplaySpeakerData = (speakerId, speakersData, anonymousDisplay) => {
    const display = isUnknownSpeakerId(speakerId) ? getAnonymousDisplay(anonymousDisplay) : null;
    if (display) {
        return { ...getUnidentifiedSpeakerData(), imageURL: '', anonymousDisplay: display };
    }
    return getSpeakerData(speakerId, speakersData) || getUnidentifiedSpeakerData();
}

export const isUnidentifiedSpeaker = (speakerData) => {
    if (!speakerData) {
        return true;
    }
    const speakerId = speakerData.id || '';
    const name = speakerData.name || '';
    return speakerId === 'unknown' || name.trim().toLowerCase() === 'unknown' || name.trim() === '';
}

// Lightweight preview records can have a name without a navigable speaker ID.
export const canOpenSpeakerProfile = (speakerData) => (
    !!speakerData?.id && !isUnidentifiedSpeaker(speakerData)
);

export const displaySpeakerName = (speakerData) => {
    if (isUnidentifiedSpeaker(speakerData)) {
        const display = isUnknownSpeakerId(speakerData?.id) ? getAnonymousDisplay(speakerData?.anonymousDisplay) : null;
        return display?.label || UNIDENTIFIED_SPEAKER_LABEL;
    }
    return speakerData.name;
}

export const displaySpeakerDescription = (speakerData) => {
    if (isUnidentifiedSpeaker(speakerData)) {
        return '';
    }
    return speakerData.description || '';
}

export const infoLink = (speakerId, speakersData) => {
    const speakerData = getSpeakerData(speakerId, speakersData)
    if (!speakerData) {
        return ''
    }
    return speakerData.moreInfoURL;
}

export const speakerName = (speakerId, speakersData) => {
    const speakerData = getSpeakerData(speakerId, speakersData)
    return displaySpeakerName(speakerData)
}

export const speakerDescription = (speakerId, speakersData) => {
    const speakerData = getSpeakerData(speakerId, speakersData)
    return displaySpeakerDescription(speakerData)
}

export const speakerImageSrc = (speakerId, speakersData) => {
    const speakerData = getSpeakerData(speakerId, speakersData)
    if (isUnidentifiedSpeaker(speakerData)) {
        return UNKNOWN_SPEAKER_IMAGE_URL
    }
    return speakerData.imageURL || ''
}
